import { forwardRef, type InputHTMLAttributes } from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '@/utils';
import { SJSpinner } from './sj-spinner';

export interface SJSearchProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  onSearch?: (value: string) => void;
  onClear?: () => void;
  loading?: boolean;
  shortcutHint?: string;
  size?: 'sm' | 'md' | 'lg';
}

const sizeStyles = {
  sm: 'h-8 text-xs px-3',
  md: 'h-10 text-sm px-4',
  lg: 'h-12 text-base px-5',
};

export const SJSearch = forwardRef<HTMLInputElement, SJSearchProps>(
  ({ className, onSearch, onClear, loading, shortcutHint, size = 'md', value, onChange, placeholder = 'What would you like to improve in your business?', ...props }, ref) => {
    const hasValue = typeof value === 'string' && value.length > 0;

    return (
      <div className={cn('relative flex items-center', className)}>
        <Search className="absolute left-3 h-4 w-4 text-[var(--sj-text-muted)] pointer-events-none" />
        <input
          ref={ref}
          type="search"
          value={value}
          onChange={onChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && typeof value === 'string') {
              onSearch?.(value);
            }
          }}
          placeholder={placeholder}
          className={cn(
            'w-full rounded-[12px] border border-[var(--sj-border)] bg-[var(--sj-surface)] pl-10 pr-20 text-[var(--sj-text-primary)] placeholder:text-[var(--sj-text-muted)] transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20',
            sizeStyles[size]
          )}
          {...props}
        />
        <div className="absolute right-3 flex items-center gap-2">
          {loading && <SJSpinner size="sm" />}
          {hasValue && !loading && (
            <button
              type="button"
              onClick={() => onClear?.()}
              className="rounded-sm p-0.5 text-[var(--sj-text-muted)] hover:text-[var(--sj-text-primary)] transition-colors"
              aria-label="Clear search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          {shortcutHint && !hasValue && !loading && (
            <kbd className="hidden sm:inline-flex items-center rounded-[6px] border border-[var(--sj-border)] bg-[var(--sj-surface-muted)] px-1.5 py-0.5 text-[0.6875rem] text-[var(--sj-text-muted)]">
              {shortcutHint}
            </kbd>
          )}
        </div>
      </div>
    );
  }
);

SJSearch.displayName = 'SJSearch';
